import React, {
    useContext,
    useCallback,
    useState,
    useRef,
    useEffect,
} from 'react';
import PropTypes from 'prop-types';
import Dots from '../../Icons/Dots/Dots';
import ModerationDelete from './ModerationDelete/ModerationDelete';
import ModerationEdit from './ModerationEdit/ModerationEdit';
import ModerationFlag from './ModerationFlag/ModerationFlag';
import ModerationAdmin from './ModerationAdmin/ModerationAdmin';

import './ModerationMenu.scss';
import {
    CommunityContext,
    PageContext,
} from '../../../contexts/ContextCreator';
import ModerationDirectLink from './ModerationDirectLink/ModerationDirectLink';

const ModerationMenu = ({
    hash,
    handleOnEditClick,
    editboxShowing,
    onMenuClick,
    disableDelete,
    onHandleDelete,
}) => {
    const { getContent } = useContext(CommunityContext);
    const { isAdmin, pageProps, wrapAuth } = useContext(PageContext);
    const content = getContent(hash);
    const isOwnContent = content.author?.userId === pageProps.session?.id;
    const contentIsComment = content?.type === 'community-comment';
    let showFlag = (content.flaggingEnabled && !isOwnContent) || false;
    const [menuOpen, setMenuOpen] = useState(false);
    const node = useRef();

    useEffect(() => {
        if (onMenuClick) {
            onMenuClick(menuOpen);
        }
    }, [menuOpen, onMenuClick]);

    if (contentIsComment && !isOwnContent) {
        showFlag = true;
    }
    const showDelete = !!content.deletable && !disableDelete;
    const showEdit =
        !!content.editable &&
        content.type !== 'community-podcast' &&
        content.type !== 'editorial-content';

    const getTitle = useCallback(() => {
        switch (content.type) {
            case 'editorial-content':
                return content.article.contentTitle;

            case 'community-podcast':
                return content.podcast.contentTitle;

            case 'community-link':
                return content.link?.previewDescription;

            default:
                return content.title || content.description;
        }
    }, [
        content.article?.contentTitle,
        content.description,
        content.link?.previewDescription,
        content.podcast?.contentTitle,
        content.title,
        content.type,
    ]);

    const authMetrics = {
        login_trigger: 'moderation-menu',
        community_content_key: `${content.type}-${content.hash}`,
        community_on_comment: content.type === 'community-comment',
    };

    const handleModMenuClick = (e) => {
        if (node?.current.contains(e.target)) {
            setMenuOpen(true);
            return;
        }
        setMenuOpen(false);
    };
    const closeMenu = () => {
        setMenuOpen(false);
    };
    useEffect(() => {
        document.addEventListener('mousedown', handleModMenuClick);
        return () => {
            document.removeEventListener('mousedown', handleModMenuClick);
        };
    }, []);

    return wrapAuth(
        `moderationMenu-${content.hash}`,
        <div className="component-ModerationMenu" ref={node}>
            <button
                aria-label="Edit"
                type="button"
                className="moderationMenu-button pt-1 pl-2"
                onClick={handleModMenuClick}
            >
                <Dots isActive={menuOpen} />
            </button>
            {menuOpen ? (
                <div className="moderationMenu-content">
                    {isAdmin && !contentIsComment ? (
                        <div className="moderationMenu-item">
                            <ModerationDirectLink
                                url={content.contentUrl}
                                closeModal={() => setMenuOpen(false)}
                            />
                        </div>
                    ) : null}
                    {showFlag ? (
                        <div className="moderationMenu-item">
                            <ModerationFlag hash={hash} />
                        </div>
                    ) : null}
                    {showEdit && !editboxShowing ? (
                        <div className="moderationMenu-item">
                            <ModerationEdit
                                hash={hash}
                                handleOnEditClick={handleOnEditClick}
                                closeMenu={closeMenu}
                            />
                        </div>
                    ) : null}
                    {showDelete ? (
                        <div className="moderationMenu-item">
                            <ModerationDelete
                                hash={hash}
                                onHandleDelete={onHandleDelete}
                            />
                        </div>
                    ) : null}
                    {isAdmin ? (
                        <div className="moderationMenu-item">
                            <ModerationAdmin
                                hash={hash}
                                title={getTitle()}
                                type={content.type}
                            />
                        </div>
                    ) : null}
                </div>
            ) : null}
        </div>,
        authMetrics
    );
};

ModerationMenu.propTypes = {
    hash: PropTypes.string.isRequired,
    handleOnEditClick: PropTypes.func,
    editboxShowing: PropTypes.bool,
};
ModerationMenu.defaultProps = {
    editboxShowing: false,
};

ModerationMenu.displayName = 'ModerationMenu';

export default ModerationMenu;
